<script>
export default {
	name: 'MHupgrade',
	inheritAttrs: false,
};
</script>

<script setup>
import MobileSubpage from '~/components/BookingWidget/MobileSubpage.vue';
import useBookingWidgetSubpage from '~/logic/composables/useBookingWidgetSubpage.js';
import SubTabUpgrade from '~/components/BookingWidget/ManageBooking/SubTabUpgrade.vue';
import subTabNeighbourFreeSeat from '~/components/BookingWidget/ManageBooking/subTabNeighbourFreeSeat.vue';
import VueTabs from '~/components/VueTabs.vue';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { useEventBus } from '@vueuse/core';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useBookingWidgetUrlQuery } from '~/logic/composables/booking-widget/useBookingWidgetUrlQuery.js';
import { pushAA_click } from '~/logic/adobe-analytic.js';

const props = defineProps({
	upgrade: { type: Object, default: () => ({}) },
	isInBookingWidget: { type: Boolean, default: false },
	general: { type: Object, default: () => null },
	neighbourFreeSeat: { type: Object, default: () => null },
	componentIdAA: { type: String, default: '' },
	togglePointsApiURL: { type: String, default: '' },
});
const emit = defineEmits([
	'update:active-sub-tab',
]);

const rootEl = ref(null);
const activeSubTab = ref('mh-upgrade');

const { isMobileSubpageVisible } = useBookingWidgetSubpage({
	hashKey: ['mh-upgrade', 'mh-upgrade-tab'],
	rootEl,
});

const { isEditorMode } = useGlobalAEMState();

//  AA for sub tab click tracking
watch(activeSubTab, (newTabName) => {
	pushAA_click([
		{
			clickName: newTabName,
			clickComponentType: 'Button',
			componentName: 'MHUpgrade',
			componentID: props.componentIdAA,
		},
		{
			name: newTabName,
			type: 'other',
		},
	]);
});


const globalBookFlightBus = useEventBus('booking-widget:book-flight');

const { registerQueryChange } = useBookingWidgetUrlQuery({
	mainTabName: 'mh-upgrade-tab',
	subTabName: 'upgrade',
});

registerQueryChange(async (query) => {
	if (!query) return;
	if (isMobileViewport.value) {
		isMobileSubpageVisible.value = true;
	}
	globalBookFlightBus.emit('active-tab-and-fill-up-form', { mainTab: query.maintab ?? '' });
	activeSubTab.value = query.subtab ?? '';
});

onMounted(() => {
	if ((!isMobileViewport.value && props.upgrade.isHideInDesktop) || (isMobileViewport.value && props.upgrade.isHideInMobile)) {
		activeSubTab.value = 'neighbour-free-seat';
	}
});

</script>

<template>
<div
	v-if="!props.isHideInDesktop"
	ref="rootEl"
	class="MHupgrade booking-widget-subcomponent"
	:class="{
		'isEditorMode': isEditorMode,
	}"
>
	<div class="generic-container">
		<h5 v-if="isEditorMode" class="title-editor-mode">
			<icon-fas-plane class="mr-2 fill-current inline-block" />
			<span>Booking Widget: MHupgrade</span>
		</h5>

		<VueTabs
			v-model="activeSubTab"
			:dormantMode="isEditorMode"
			renderPanelsWithVShow
			:hideTabs="props.general?.hideSubtabs"
			@update:modelValue="$emit('update:active-sub-tab', $event)"
		>
			<template v-if="!props.upgrade?.isHideInDesktop" #tab-mh-upgrade>
				<div class="flex items-center justify-center p-4">
					<span class="inline-block ml-1">{{ props.upgrade?.label }}</span>
				</div>
			</template>
			<template v-if="!props.neighbourFreeSeat?.isHideInDesktop" #tab-neighbour-free-seat>
				<div class="flex items-center justify-center p-4">
					<span class="inline-block ml-1">{{ props.neighbourFreeSeat?.label }}</span>
				</div>
			</template>

			<div
				v-if="!props.upgrade?.isHideInDesktop && props.upgrade?.label"
				identifier="mh-upgrade"
			>
				<SubTabUpgrade
					v-if="!props.upgrade?.isHideInDesktop"
					:class="{ 'pt-16': props.upgrade.isToggle }"
					:apiUrl="props.upgrade.apiUrl"
					:apiPointsUrl="props.upgrade.apiPointsUrl"
					:bookingReference="props.upgrade.bookingReference"
					:lastName="props.upgrade.lastName"
					:description="props.upgrade.description"
					:tooltipLabel="props.upgrade.tooltipLabel"
					:tooltipBody="props.upgrade.tooltipBody"
					:buttonLabel="props.upgrade.buttonLabel"
					:note="props.upgrade.note"
					:isToggle="props.upgrade.isToggle"
					:toggleButtonLabel="props.upgrade.toggleButtonLabel"
					:togglePointsApiURL="props.togglePointsApiURL"
					:isMainTab="true"
					:enableGlassDesign="props.upgrade?.enableGlassDesign"
				></SubTabUpgrade>
			</div>
			<div
				v-if="!props.neighbourFreeSeat?.isHideInDesktop && props.neighbourFreeSeat?.label"
				identifier="neighbour-free-seat"
			>
				<subTabNeighbourFreeSeat
					v-if="!props.neighbourFreeSeat?.isHideInDesktop"
					:apiUrl="props.upgrade.apiUrl"
					:nfsEndpointUrl="props.neighbourFreeSeat.nfsEndpointUrl"
					:apiPointsUrl="props.upgrade.apiPointsUrl"
					:bookingReference="props.neighbourFreeSeat.bookingReference"
					:lastName="props.neighbourFreeSeat.lastName"
					:description="props.neighbourFreeSeat.description"
					:tooltipLabel="props.neighbourFreeSeat.tooltipLabel"
					:tooltipBody="props.neighbourFreeSeat.tooltipBody"
					:buttonLabel="props.neighbourFreeSeat.buttonLabel"
					:note="props.neighbourFreeSeat.note"
					:toggleButtonLabel="props.neighbourFreeSeat.toggleButtonLabel"
					:enableGlassDesign="props.neighbourFreeSeat.enableGlassDesign"
					:togglePointsApiURL="props.togglePointsApiURL"
					:isMainTab="false"
				></subTabNeighbourFreeSeat>
			</div>
		</VueTabs>
	</div>
</div>

<MobileSubpage
	:isVisible="isMobileSubpageVisible && !props.upgrade?.enableGlassDesign"
	class="text-white"
	style="--overlay-container-bg-color: var(--secondary-blue-extradark);"
	@update:is-visible="isMobileSubpageVisible = $event"
>

	<template #banner-title>
		<h1 class="text-center">{{ $t('MHupgrade') }}</h1>
	</template>

	<template #default>
		<div class="generic-container mb-20">
			<VueTabs
				v-model="activeSubTab"
				:dormantMode="isEditorMode"
				:hideTabs="props.general?.hideSubtabs"
				renderPanelsWithVShow
				@update:modelValue="$emit('update:active-sub-tab', $event)"
			>
				<template v-if="!props.upgrade?.isHideInMobile" #tab-mh-upgrade>
					<div class="flex items-center justify-center p-4">
						<span class="inline-block ml-1">{{ props.upgrade?.label }}</span>
					</div>
				</template>
				<template v-if="!props.neighbourFreeSeat?.isHideInMobile" #tab-neighbour-free-seat>
					<div class="flex items-center justify-center p-4">
						<span class="inline-block ml-1">{{ props.neighbourFreeSeat?.label }}</span>
					</div>
				</template>

				<div identifier="mh-upgrade">
					<SubTabUpgrade
						v-if="!props.upgrade?.isHideInMobile"
						:class="{ 'pt-16': props.upgrade.isToggle }"
						:apiUrl="props.upgrade.apiUrl"
						:apiPointsUrl="props.upgrade.apiPointsUrl"
						:bookingReference="props.upgrade.bookingReference"
						:lastName="props.upgrade.lastName"
						:description="props.upgrade.description"
						:tooltipLabel="props.upgrade.tooltipLabel"
						:tooltipBody="props.upgrade.tooltipBody"
						:buttonLabel="props.upgrade.buttonLabel"
						:note="props.upgrade.note"
						:isToggle="props.upgrade.isToggle"
						:toggleButtonLabel="props.upgrade.toggleButtonLabel"
						:togglePointsApiURL="props.togglePointsApiURL"
						:isMainTab="true"
						:enableGlassDesign="props.upgrade?.enableGlassDesign"
					></SubTabUpgrade>
				</div>
				<div identifier="neighbour-free-seat">
					<subTabNeighbourFreeSeat
						v-if="!props.neighbourFreeSeat?.isHideInMobile"
						:apiUrl="props.upgrade.apiUrl"
						:apiPointsUrl="props.upgrade.apiPointsUrl"
						:bookingReference="props.neighbourFreeSeat.bookingReference"
						:lastName="props.neighbourFreeSeat.lastName"
						:description="props.neighbourFreeSeat.description"
						:tooltipLabel="props.neighbourFreeSeat.tooltipLabel"
						:tooltipBody="props.neighbourFreeSeat.tooltipBody"
						:buttonLabel="props.neighbourFreeSeat.buttonLabel"
						:note="props.neighbourFreeSeat.note"
						:toggleButtonLabel="props.neighbourFreeSeat.toggleButtonLabel"
						:enableGlassDesign="props.neighbourFreeSeat.enableGlassDesign"
						:togglePointsApiURL="props.togglePointsApiURL"
						:isMainTab="false"
					></subTabNeighbourFreeSeat>
				</div>
			</VueTabs>
		</div>
	</template>

</MobileSubpage>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.MHupgrade {

}

</style>
